import { api, Badge, Banner, dateTime, Dialog, Empty, Field, PageHead, Select, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";
import { PERIODS } from "../lib/schedule";

interface Rule {
  id: string;
  builtin: string | null;
  title: string;
  comparison: "above" | "below";
  threshold: number;
  recipients: string[];
  chatWebhook: string | null;
  quietMinutes: number;
  active: boolean;
  lastFiredAt: string | null;
  period: string | null;
}

interface Event {
  id: string;
  ruleId: string;
  title: string;
  message: string;
  at: string;
  delivered: string;
}

const UNITS: Record<string, string> = { no_sale: "hours", low_margin: "%", refunds_today: "EUR", failed_orders: "orders", supplier_failures: "failures", sync_lag: "minutes" };

/** Measures a custom rule can watch: [table, measure, label]. */
const MEASURES: [string, string, string][] = [
  ["orders", "orders", "Orders"],
  ["orders", "sell", "Sold (EUR)"],
  ["orders", "margin", "Margin (EUR)"],
  ["orders", "margin_pct", "Margin %"],
  ["orders", "outstanding", "Still to pay (EUR)"],
  ["items", "room_nights", "Hotel room nights"],
  ["movements", "amount", "Refunds (EUR)"],
];

function RuleRow({ rule, manage, onSaved }: { rule: Rule; manage: boolean; onSaved: (text: string, tone?: "ok" | "bad") => void }) {
  const [threshold, setThreshold] = useState(String(rule.threshold));
  const [recipients, setRecipients] = useState(rule.recipients.join(", "));
  const save = async (patch: Record<string, unknown>) => {
    try {
      await api("PUT", `/v1/alerts/${rule.id}`, patch);
      onSaved(`“${rule.title}” saved.`);
    } catch (err) {
      onSaved(err instanceof Error ? err.message : String(err), "bad");
    }
  };
  const unit = rule.builtin ? UNITS[rule.builtin] : rule.period ? PERIODS[rule.period]?.toLowerCase() : "";
  return (
    <tr>
      <td>{rule.title}{!rule.builtin && <div className="small muted">{rule.comparison} the threshold, {unit}</div>}</td>
      <td className="small">{manage ? <span className="row" style={{ gap: 4 }}><input style={{ width: 90 }} value={threshold} onChange={(e) => setThreshold(e.target.value)} /> {rule.builtin ? unit : ""}</span> : `${rule.threshold} ${rule.builtin ? unit : ""}`}</td>
      <td className="small">{manage ? <input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="Default recipients" /> : rule.recipients.join(", ") || "Default recipients"}</td>
      <td className="small">{rule.lastFiredAt ? dateTime(rule.lastFiredAt, "Europe/Lisbon") : "—"}</td>
      <td>{rule.active ? <Badge tone="ok">on</Badge> : <Badge tone="neutral">off</Badge>}</td>
      {manage && (
        <td className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
          <button className="ghost" onClick={() => void save({ threshold: Number(threshold), recipients: recipients.split(/[\s,;]+/).filter(Boolean) })}>Save</button>
          <button className="ghost" onClick={() => void save({ active: !rule.active })}>{rule.active ? "Switch off" : "Switch on"}</button>
        </td>
      )}
    </tr>
  );
}

function NewRule({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState("");
  const [measure, setMeasure] = useState("1");
  const [period, setPeriod] = useState("today");
  const [comparison, setComparison] = useState<"above" | "below">("above");
  const [threshold, setThreshold] = useState("");
  const [recipients, setRecipients] = useState("");
  const [error, setError] = useState<string>();
  const save = async () => {
    const [table, key] = MEASURES[Number(measure)]!;
    try {
      await api("POST", "/v1/alerts", { title, spec: { table, measures: [key], filter: { status: "sales" }, ...(table === "movements" ? { kinds: ["refund", "chargeback"] } : {}) }, period, comparison, threshold: Number(threshold), recipients: recipients.split(/[\s,;]+/).filter(Boolean) });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  return (
    <Dialog title="New alert" description="Checked every five minutes; money in euros." onClose={onClose} onSubmit={() => void save()} width={520} actions={<><button type="button" className="ghost" onClick={onClose}>Cancel</button><button type="submit">Save</button></>}>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      <Field label="Name"><input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={80} placeholder="Sales of the day below target" /></Field>
      <div className="grid2">
        <Field label="Measure"><Select value={measure} onChange={setMeasure} options={MEASURES.map(([, , label], i) => ({ value: String(i), label }))} /></Field>
        <Field label="Over"><Select value={period} onChange={setPeriod} options={Object.entries(PERIODS).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="When it is"><Select<"above" | "below"> value={comparison} onChange={setComparison} options={[{ value: "above", label: "Above" }, { value: "below", label: "Below" }]} /></Field>
        <Field label="Threshold"><input type="number" step="any" value={threshold} onChange={(e) => setThreshold(e.target.value)} required /></Field>
      </div>
      <Field label="Email to" help="Empty: the default recipients."><input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="name@ideastation.eu" /></Field>
    </Dialog>
  );
}

/** Alerts (docs/36 §10): the rules, what fired, and for managers the thresholds and recipients. */
export function AlertsPage() {
  const manage = useCan()("alerts:manage");
  const { data, error, reload } = useAsync(() => api<{ rules: Rule[]; events: Event[]; defaults: { emails: string[]; chat: boolean }; sending: boolean }>("GET", "/v1/alerts"), []);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string }>();
  const [adding, setAdding] = useState(false);
  const saved = (text: string, tone: "ok" | "bad" = "ok") => {
    setNotice({ tone, text });
    reload();
  };
  const check = async () => {
    const r = await api<{ fired: number }>("POST", "/v1/alerts/check");
    saved(r.fired ? `${r.fired} alert${r.fired === 1 ? "" : "s"} fired.` : "Nothing fired.");
  };
  return (
    <>
      <PageHead title="Alerts" sub={data ? `Default recipients: ${[...data.defaults.emails, ...(data.defaults.chat ? ["Google Chat"] : [])].join(", ") || "none set"}${data.sending ? "" : " · sending is off on this server"}` : undefined}>
        {manage && <><button className="ghost" onClick={() => void check()}>Check now</button><button onClick={() => setAdding(true)}>New alert</button></>}
      </PageHead>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      <div className="card" style={{ padding: 0, marginBottom: 14 }}>
        <table ref={stackOnMobile} className="table stack-mobile">
          <thead><tr><th>Rule</th><th>Threshold</th><th>Email to</th><th>Last fired</th><th>State</th>{manage && <th />}</tr></thead>
          <tbody>{(data?.rules ?? []).map((r) => <RuleRow key={`${r.id}:${r.threshold}:${r.active}:${r.recipients.join()}`} rule={r} manage={manage} onSaved={saved} />)}</tbody>
        </table>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}><h2 style={{ margin: 0, fontSize: 15 }}>What fired</h2></div>
        {data && data.events.length === 0 ? <Empty>Nothing has fired yet.</Empty> : (
          <table ref={stackOnMobile} className="table stack-mobile">
            <thead><tr><th>When</th><th>Rule</th><th>What</th><th>Sent</th></tr></thead>
            <tbody>
              {(data?.events ?? []).map((e) => (
                <tr key={e.id}><td className="small">{dateTime(e.at, "Europe/Lisbon")}</td><td className="small">{e.title}</td><td>{e.message}</td><td className="small muted">{e.delivered}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && <NewRule onClose={() => setAdding(false)} onSaved={reload} />}
    </>
  );
}
